import { EditFields, FlightChanges, FlightEditDto } from "../../models/flights.model";
import { Flight } from "../../models/flight.model";
import { FlightEditForm } from "../form/flight-edit-form";
import { FlightEditLookups } from "../form/flight-edit-lookups";
import { durationsOf } from "../tabs/flight/times-group/times-group";
import { FlightEditSnapshot, diff, rowChanges, snapshotOf } from "./diff";

export interface EditRequestSource {
    flight: Flight;
    form: FlightEditForm;
    initial: FlightEditSnapshot;
    lookups: FlightEditLookups;
    reason: string;
    idempotencyKey: string;
}

/** The columns of `Flights` that the dialog edits; the others it edits belong to `HTLFlights`. */
const FLIGHTS_COLUMNS = ["flightTypeId", "isPositioning", "departureId", "arrivalId", "pilotId", "pilotRoleId", "coPilotId", "coPilotRoleId", "coPilotName"];

/** The days and times of the flight: when any of them changes, so do its flight time and engine time. */
const MOMENT_COLUMNS = ["startDate", "flightDate", "arrivalDate", "stopDate", "startTime", "takeOffTime", "landingTime", "stopTime"];

/** The moments each duration is worked out from, as `minutesOf` reads them. */
const FLIGHT_TIME_MOMENTS = ["flightDate", "takeOffTime", "arrivalDate", "landingTime"];
const ENGINE_TIME_MOMENTS = ["startDate", "flightDate", "startTime", "stopDate", "arrivalDate", "stopTime"];

/** The lists of the organization the logbook keeps as `1,2,4`. */
const LIST_COLUMNS = ["flightOperations", "flightSpecifications", "flightTypeNotes"];

/**
 * Only the values that differ from the dialog's opening ones, rows included, named as the entities of `helilog-database`:
 * `htlFlight` holds the columns of `HTLFlights`, and each part has its relation's name (`flight`, `htlFlightN1N2Imp`,
 * `enginePowerCheck`, `htlPenalityFactor`, `htlFlightHook` ...). An id travels with the text the entity keeps next to it.
 */
export function buildEditRequest(source: EditRequestSource): FlightEditDto {
    const { flight: current, form, initial, lookups } = source;
    const now = snapshotOf(form);
    const { coPilot, ...changes } = diff(initial.flight, now.flight);
    const htlFlight: FlightChanges = { id: current.id, requestCode: current.requestCode };
    const flight: EditFields = {};
    const engineData: EditFields = {};
    for (const [column, value] of Object.entries(changes)) {
        if (column.startsWith("engine")) engineData[column] = value;
        else if (LIST_COLUMNS.includes(column)) flight[column] = (value as number[]).join(",");
        else if (FLIGHTS_COLUMNS.includes(column)) flight[column] = value;
        else htlFlight[column] = value;
    }
    // The fuel the flight keeps is part of the flight itself: its changes go with the others, by their columns.
    for (const [column, value] of Object.entries(diff(initial.fuel, now.fuel))) htlFlight[column] = value;
    // Both follow from the days and times, so they travel with any of them, even when their value comes out the same.
    if (MOMENT_COLUMNS.some((column) => column in changes)) {
        const { flightTime, startStopTime } = durationsOf(form.controls.flight.getRawValue());
        htlFlight["flightTime"] = flightTime;
        htlFlight["startStopTime"] = startStopTime;
    }
    const roleOf = (id: unknown) => lookups.pilotRoles.find((role) => role.id === id)?.abbreviation ?? null;
    if ("pilotId" in changes) htlFlight["pilot1Name"] = lookups.pilots.find((pilot) => pilot.id === changes["pilotId"])?.name ?? null;
    if ("pilotRoleId" in changes) htlFlight["pilot1Role"] = roleOf(changes["pilotRoleId"]);
    if ("coPilotRoleId" in changes) htlFlight["pilot2Role"] = roleOf(changes["coPilotRoleId"]);
    if ("flightTypeId" in changes) htlFlight["flightType"] = lookups.flightTypes.find((type) => type.id === changes["flightTypeId"])?.name ?? null;
    if ("departureId" in changes) htlFlight["departurePlace"] = lookups.airportOf(changes["departureId"] as number | null)?.label ?? null;
    if ("arrivalId" in changes) htlFlight["arrivalPlace"] = lookups.airportOf(changes["arrivalId"] as number | null)?.label ?? null;
    // The copilot may be a pilot of the organization (`coPilotId`) or a name typed by hand (`Flights.coPilotName`).
    if (coPilot !== undefined) {
        const value = form.controls.flight.controls.coPilot.value;
        flight["coPilotId"] = value?.id ?? null;
        flight["coPilotName"] = value?.id ? null : (value?.name ?? null);
        htlFlight["pilot2Name"] = value?.name ?? null;
    }
    const penalities = diff(initial.penaltyFactor, now.penaltyFactor);
    const nested = {
        flight: flight,
        htlFlightN1N2Imp: engineData,
        enginePowerCheck: diff(initial.enginePowerCheck, now.enginePowerCheck),
        htlPenalityFactor: penalities,
        htlFlightHook: rowChanges(initial.hooks, now.hooks),
        htlFlightHoist: rowChanges(initial.hoists, now.hoists),
        refuellingBeforeFirstFlight: rowChanges(initial.refuels, now.refuels),
        oilSuppliedBeforeNextFlight: rowChanges(initial.oils, now.oils),
        htlFlightCrew: rowChanges(initial.crews, now.crews),
    };
    for (const [key, value] of Object.entries(nested)) {
        if (Array.isArray(value) ? value.length : Object.keys(value).length) htlFlight[key] = value;
    }
    return { idempotencyKey: source.idempotencyKey, reason: source.reason.trim(), htlFlight: htlFlight };
}

/**
 * What the user changed, as they see it: one for each field they touched. The calculated values count too (the total
 * fuel, the flight and engine times), but not the texts that only repeat a pick (the flight type's name next to its id).
 */
export function userChangeCountOf(source: EditRequestSource): number {
    const { form, initial } = source;
    const now = snapshotOf(form);
    const rows = (before: Parameters<typeof rowChanges>[0], after: Parameters<typeof rowChanges>[1]) =>
        rowChanges(before, after).reduce((count, row) => count + (row.deleted ? 1 : Object.keys(row).length - 1), 0);
    const fields = (before: object, after: object) => Object.keys(diff(before as never, after as never)).length;
    // The flight time and the engine time are worked out, yet they are changes as well: the request carries both.
    const moved = diff(initial.flight, now.flight);
    const dependsOn = (columns: string[]) => (columns.some((column) => column in moved) ? 1 : 0);
    const durations = dependsOn(FLIGHT_TIME_MOMENTS) + dependsOn(ENGINE_TIME_MOMENTS);
    return (
        durations +
        fields(initial.flight, now.flight) +
        fields(initial.fuel, now.fuel) +
        fields(initial.enginePowerCheck, now.enginePowerCheck) +
        fields(initial.penaltyFactor, now.penaltyFactor) +
        rows(initial.hooks, now.hooks) +
        rows(initial.hoists, now.hoists) +
        rows(initial.refuels, now.refuels) +
        rows(initial.oils, now.oils) +
        rows(initial.crews, now.crews)
    );
}
